import React, { useEffect, useState } from "react";
import api from "../../../utils/Url";
import { Chart } from "react-google-charts";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  Typography,
  Box,
  IconButton,
  useTheme,
  Paper,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import { motion } from "framer-motion";
import type { ReactGoogleChartEvent } from "react-google-charts";
import {
  DataGrid,
  GridColDef,
  GridToolbar,
} from "@mui/x-data-grid";
import { useTranslation } from "react-i18next";

interface FileWard {
  wardID: number;
  wardName: string;
  fileCount: number;
  fileNames: string[];
}

interface Zone {
  zoneID: number;
  zoneName: string;
  wardCount: number;
  wards: FileWard[];
}

const DotLoader = () => {
  return (
    <Box
      display="flex"
      alignItems="center"
      justifyContent="center"
      height="200px"
      position="relative"
      width={80}
      mx="auto"
    >
      {[0, 1, 2].map((i) => (
      <motion.div
        key={i}
        style={{
        position: 'absolute',
        left: `${i * 30}px`,
        width: 15,
        height: 15,
        borderRadius: '50%',
        backgroundColor: '#0D47A1', // Blue 900
        }}
        animate={{
        opacity: [0.2, 1, 0.2],
        scale: [0.8, 1.2, 0.8],
        }}
        transition={{
        duration: 0.8,
        repeat: Infinity,
        repeatDelay: 0,
        delay: i * 0.4,
        ease: "easeInOut"
        }}
      />
      ))}
    </Box>
  );
};

const ZonePieChart: React.FC = () => {
  const theme = useTheme();
  const { t } = useTranslation();
  const [zoneData, setZoneData] = useState<Zone[]>([]);
  const [loading, setLoading] = useState<boolean>(true);
  const [modalOpen, setModalOpen] = useState<boolean>(false);
  const [selectedZone, setSelectedZone] = useState<Zone | null>(null);
  const [paginationModel, setPaginationModel] = useState({
    page: 0,
    pageSize: 10,
  });

  useEffect(() => {
    const fetchData = async () => {
      try {
        const res = await api.get("Dashboard/GetDashboardZoneWithFiles");
        if (res.data?.isSuccess) {
          setZoneData(res.data.data);
        }
      } catch (error) {
        console.error("Error fetching zone data:", error);
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, []);

  const chartData = [
    ["Zone", "Ward Count"],
    ...zoneData.map((zone) => [zone.zoneName, zone.wardCount]),
  ];

  const handleSliceClick = (zoneName: string) => {
    const zone = zoneData.find((z) => z.zoneName === zoneName);
    if (zone) {
      setSelectedZone(zone);
      setModalOpen(true);
      // Reset pagination when opening modal
      setPaginationModel(prev => ({ ...prev, page: 0 }));
    }
  };

  const chartEvents: ReactGoogleChartEvent[] = [
    {
      eventName: "select",
      callback: ({ chartWrapper }: any) => {
        const chart = chartWrapper.getChart();
        const selection = chart.getSelection();
        if (selection.length > 0) {
          const selectedItem = selection[0];
          const zoneName: any = chartData[selectedItem.row + 1][0];
          handleSliceClick(zoneName);
        }
      },
    },
  ];

  const columns: GridColDef[] = [
    {
      field: "wardName",
      headerName: t("text.WardName"),
      flex: 1,
      minWidth: 200,
      headerClassName: "super-app-theme--header",
    },
    {
      field: "fileCount",
      headerName: t("text.FileCount"),
      flex: 1,
      width: 120,
      headerAlign: "center",
      align: "center",
      headerClassName: "super-app-theme--header",
    },
    {
      field: "fileNames",
      headerName: t("text.files"),
      flex: 2,
      minWidth: 300,
      headerClassName: "super-app-theme--header",
      renderCell: (params) => (
        <Box
          sx={{
            display: "flex",
            flexWrap: "wrap",
            gap: 0.5,
            maxHeight: "100px",
            overflow: "auto",
            alignItems: "center",
          }}
        >
          {Array.isArray(params.value) && params.value.length > 0 ? (
            params.value.map((file: string, idx: number) => (
              <Typography
                key={idx}
                variant="body2"
                sx={{
                  backgroundColor: theme.palette.grey[300],
                  px: 1,
                  borderRadius: 1,
                }}
              >
                {file}
              </Typography>
            ))
          ) : (
            <Typography variant="body2" color="text.secondary">
              No files available
            </Typography>
          )}
        </Box>
      ),
    },
  ];

  return (
    <Box
      width="100%"
      component={motion.div}
      initial={{ opacity: 0, y: 30 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.6, ease: "easeOut" }}
      sx={{ p: { xs: 1, sm: 2, md: 3 }, display: "flex", justifyContent: "center" }}
    >
      {loading ? (
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: '200px' }}>
          <DotLoader />
        </Box>
      ) : (
        <Box
          sx={{
            width: "100%",
            maxWidth: "1000px",
            aspectRatio: "2 / 1",
            px: { xs: 1, md: 4 },
          }}
        >
          <motion.div
            initial={{ opacity: 0, scale: 0.95 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ duration: 0.5 }}
            style={{ width: "100%", height: "100%" }}
          >
            <Chart
              chartType="PieChart"
              width="100%"
              height="100%"
              data={chartData}
              options={{
                title: "Zone-wise Ward Count",
                pieHole: 0.4,
                legend: { position: "right", textStyle: { fontSize: 14 } },
                chartArea: { width: "100%", height: "90%" },
                pieSliceTextStyle: { color: "black" },
              }}
              chartEvents={chartEvents}
            />
          </motion.div>
        </Box>
      )}

      {/* Modal */}
      <Dialog
        open={modalOpen}
        onClose={() => setModalOpen(false)}
        maxWidth="lg"
        fullWidth
        PaperProps={{
          sx: {
            height: "70vh",
          },
        }}
      >
        <DialogTitle
          sx={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            backgroundColor: theme.palette.primary.main,
            color: theme.palette.primary.contrastText,
            py: 1,
            px: 2,
          }}
        >
          <Typography variant="h6" sx={{ fontSize: '1.1rem' }}>
            {selectedZone?.zoneName} - {t("text.WardDetails")}
          </Typography>
          <IconButton onClick={() => setModalOpen(false)} color="inherit" sx={{ p: 0.5 }}>
            <CloseIcon />
          </IconButton>
        </DialogTitle>
        <DialogContent sx={{ p: 1, mt: 1, display: "flex", flexDirection: "column" }}>
          {selectedZone?.wards?.length ? (
            <Paper
              sx={{
                height: "100%",
                display: "flex",
                flexDirection: "column",
                "& .super-app-theme--header": {
                  backgroundColor: theme.palette.primary.main,
                  color: theme.palette.primary.contrastText,
                  fontWeight: "bold",
                  fontSize: '1rem',
                  padding: '8px 16px',
                },
              }}
            >
              <DataGrid
                rows={selectedZone.wards}
                columns={columns}
                getRowId={(row) => row.wardID}
                paginationModel={paginationModel}
                onPaginationModelChange={setPaginationModel}
                pageSizeOptions={[5, 10, 25, 50, 100]}
                sx={{
                  flex: 1,
                  "& .MuiDataGrid-cell": {
                    borderBottom: `1px solid ${theme.palette.divider}`,
                  },
                  "& .MuiDataGrid-virtualScroller": {
                    overflow: "auto",
                  },
                }}
              />
            </Paper>
          ) : (
            <Typography variant="body2" color="text.secondary" sx={{ p: 2 }}>
              No ward data available.
            </Typography>
          )}
        </DialogContent>
      </Dialog>
    </Box>
  );
};

export default ZonePieChart;




















// import React, { useEffect, useState } from "react";
// import api from "../../../utils/Url";
// import { Chart } from "react-google-charts";
// import {
//   Dialog,
//   DialogTitle,
//   DialogContent,
//   Table,
//   TableBody,
//   TableCell,
//   TableContainer,
//   TableHead,
//   TableRow,
//   Typography,
//   Box,
//   CircularProgress,
//   Paper,
//   IconButton,
//   TablePagination,
//   useTheme,
// } from "@mui/material";
// import CloseIcon from "@mui/icons-material/Close";
// import { motion } from "framer-motion";
// import type { ReactGoogleChartEvent } from "react-google-charts";

// interface FileWard {
//   wardID: number;
//   wardName: string;
//   fileCount: number;
//   fileNames: string[];
// }

// interface Zone {
//   zoneID: number;
//   zoneName: string;
//   wardCount: number;
//   wards: FileWard[];
// }

// const ZonePieChart: React.FC = () => {
//   const theme = useTheme();
//   const [zoneData, setZoneData] = useState<Zone[]>([]);
//   const [loading, setLoading] = useState<boolean>(true);
//   const [modalOpen, setModalOpen] = useState<boolean>(false);
//   const [selectedZone, setSelectedZone] = useState<Zone | null>(null);
//   const [page, setPage] = useState(0);
//   const [rowsPerPage, setRowsPerPage] = useState(5);

//   useEffect(() => {
//     const fetchData = async () => {
//       try {
//         const res = await api.get("Dashboard/GetDashboardZoneWithFiles");
//         if (res.data?.isSuccess) {
//           setZoneData(res.data.data);
//         }
//       } catch (error) {
//         console.error("Error fetching zone data:", error);
//       } finally {
//         setLoading(false);
//       }
//     };
//     fetchData();
//   }, []);

//   const chartData = [
//     ["Zone", "Ward Count"],
//     ...zoneData.map((zone) => [zone.zoneName, zone.wardCount]),
//   ];

//   const handleSliceClick = (zoneName: string) => {
//     const zone = zoneData.find((z) => z.zoneName === zoneName);
//     if (zone) {
//       setSelectedZone(zone);
//       setPage(0);
//       setModalOpen(true);
//     }
//   };

//   const chartEvents: ReactGoogleChartEvent[] = [
//     {
//       eventName: "select",
//       callback: ({ chartWrapper }: any) => {
//         const chart = chartWrapper.getChart();
//         const selection = chart.getSelection();
//         if (selection.length > 0) {
//           const selectedItem = selection[0];
//           const zoneName: any = chartData[selectedItem.row + 1][0];
//           handleSliceClick(zoneName);
//         }
//       },
//     },
//   ];

//   const handleChangePage = (_: unknown, newPage: number) => setPage(newPage);
//   const handleChangeRowsPerPage = (e: React.ChangeEvent<HTMLInputElement>) => {
//     setRowsPerPage(parseInt(e.target.value, 10));
//     setPage(0);
//   };

//   return (
//     <Box
//       width="100%"
//       component={motion.div}
//       initial={{ opacity: 0, y: 30 }}
//       animate={{ opacity: 1, y: 0 }}
//       transition={{ duration: 0.6, ease: "easeOut" }}
//       sx={{ p: { xs: 1, sm: 2, md: 3 }, display: "flex", justifyContent: "center" }}
//     >
//       {loading ? (
//         <CircularProgress />
//       ) : (
//         <Box
//           sx={{
//             width: "100%",
//             maxWidth: "1000px",
//             aspectRatio: "2 / 1",
//             px: { xs: 1, md: 4 },
//           }}
//         >
//           <motion.div
//             initial={{ opacity: 0, scale: 0.95 }}
//             animate={{ opacity: 1, scale: 1 }}
//             transition={{ duration: 0.5 }}
//             style={{ width: "100%", height: "100%" }}
//           >
//             <Chart
//               chartType="PieChart"
//               width="100%"
//               height="100%"
//               data={chartData}
//               options={{
//                 title: "Zone-wise Ward Count",
//                 pieHole: 0.4,
//                 legend: { position: "right", textStyle: { fontSize: 14 } },
//                 chartArea: { width: "100%", height: "90%" },
//                 pieSliceTextStyle: { color: "black" },
//               }}
//               chartEvents={chartEvents}
//             />
//           </motion.div>
//         </Box>
//       )}

//       {/* Modal */}
//       <Dialog
//         open={modalOpen}
//         onClose={() => setModalOpen(false)}
//         maxWidth="lg"
//         fullWidth
//       >
//         <DialogTitle
//           sx={{
//             display: "flex",
//             justifyContent: "space-between",
//             alignItems: "center",
//             backgroundColor: theme.palette.primary.main,
//             color: theme.palette.primary.contrastText,
//           }}
//         >
//           <Typography variant="h6">
//             {selectedZone?.zoneName} - Ward Details
//           </Typography>
//           <IconButton onClick={() => setModalOpen(false)} color="inherit">
//             <CloseIcon />
//           </IconButton>
//         </DialogTitle>
//         <DialogContent sx={{ p: 0 }}>
//           {selectedZone?.wards?.length ? (
//             <>
//               <TableContainer component={Paper}>
//                 <Table size="small" stickyHeader>
//                   <TableHead>
//                     <TableRow sx={{ backgroundColor: theme.palette.grey[200] }}>
//                       <TableCell sx={{ minWidth: "200px" }}><strong>Ward Name</strong></TableCell>
//                       <TableCell sx={{ minWidth: "160px" }}><strong>File Count</strong></TableCell>
//                       <TableCell><strong>Files</strong></TableCell>
//                     </TableRow>
//                   </TableHead>
//                   <TableBody>
//                     {selectedZone.wards
//                       .slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage)
//                       .map((ward) => (
//                         <TableRow key={ward.wardID}>
//                           <TableCell sx={{ minWidth: "200px" }}>{ward.wardName}</TableCell>
//                           <TableCell sx={{ minWidth: "160px" }}>{ward.fileCount}</TableCell>
//                           <TableCell>
//                             {ward.fileNames.length > 0 ? (
//                               <div style={{ display: "flex" }}>
//                                 {ward.fileNames.map((file, idx) => (
//                                   <Typography variant="body2">{file + ", "}</Typography>
//                                 ))}
//                               </div>
//                             ) : (
//                               <Typography variant="body2" color="text.secondary">
//                                 No files available.
//                               </Typography>
//                             )}
//                           </TableCell>
//                         </TableRow>
//                       ))}
//                   </TableBody>
//                 </Table>
//               </TableContainer>
//               <TablePagination
//                 component="div"
//                 count={selectedZone.wards.length}
//                 page={page}
//                 onPageChange={handleChangePage}
//                 rowsPerPage={rowsPerPage}
//                 onRowsPerPageChange={handleChangeRowsPerPage}
//                 rowsPerPageOptions={[5, 10, 15]}
//                 sx={{
//                   px: 2,
//                   borderTop: `1px solid ${theme.palette.divider}`,
//                 }}
//               />
//             </>
//           ) : (
//             <Typography variant="body2" color="text.secondary" sx={{ p: 2 }}>
//               No ward data available.
//             </Typography>
//           )}
//         </DialogContent>
//       </Dialog>
//     </Box>
//   );
// };

// export default ZonePieChart;

















// import React, { useEffect, useState } from "react";
// import api from "../../../utils/Url";
// import { Chart } from "react-google-charts";
// import {
//   Dialog,
//   DialogTitle,
//   DialogContent,
//   DialogContentText,
//   List,
//   ListItem,
//   ListItemText,
//   Typography,
//   Box,
//   CircularProgress,
// } from "@mui/material";
// import type { ReactGoogleChartEvent } from "react-google-charts";


// interface FileWard {
//   wardID: number;
//   wardName: string;
//   fileCount: number;
//   fileNames: string[];
// }

// interface Zone {
//   zoneID: number;
//   zoneName: string;
//   wardCount: number;
//   wards: FileWard[];
// }

// const ZonePieChart: React.FC = () => {
//   const [zoneData, setZoneData] = useState<Zone[]>([]);
//   const [loading, setLoading] = useState<boolean>(true);
//   const [modalOpen, setModalOpen] = useState<boolean>(false);
//   const [selectedZone, setSelectedZone] = useState<Zone | null>(null);

//   useEffect(() => {
//     const fetchData = async () => {
//       try {
//         const res = await api.get("Dashboard/GetDashboardZoneWithFiles");
//         if (res.data?.isSuccess) {
//           setZoneData(res.data.data);
//         }
//       } catch (error) {
//         console.error("Error fetching zone data:", error);
//       } finally {
//         setLoading(false);
//       }
//     };
//     fetchData();
//   }, []);

//   const chartData = [
//     ["Zone", "Ward Count"],
//     ...zoneData.map((zone) => [zone.zoneName, zone.wardCount]),
//   ];

//   const handleSliceClick = (zoneName: string) => {
//     const zone = zoneData.find((z) => z.zoneName === zoneName);
//     if (zone) {
//       setSelectedZone(zone);
//       setModalOpen(true);
//     }
//   };

//   const chartOptions = {
//     title: "Zone-wise Ward Count",
//     pieHole: 0.4,
//     legend: { position: "right" },
//     chartArea: { width: "90%", height: "80%" },
//   };


//   const chartEvents: ReactGoogleChartEvent[] = [
//     {
//       eventName: "select" as const,
//       callback: ({ chartWrapper }: any) => {
//         const chart = chartWrapper.getChart();
//         const selection = chart.getSelection();
//         if (selection.length > 0) {
//           const selectedItem = selection[0];
//           const zoneName: any = chartData[selectedItem.row + 1][0];
//           handleSliceClick(zoneName);
//         }
//       },
//     },
//   ];

//   return (
//     <Box display="flex" flexDirection="column" alignItems="center">

//       {loading ? (
//         <CircularProgress />
//       ) : (
//         <Chart
//           chartType="PieChart"
//           width="100%"
//           height="400px"
//           data={chartData}
//           options={chartOptions}
//           chartEvents={chartEvents}
//         />
//       )}

//       <Dialog open={modalOpen} onClose={() => setModalOpen(false)} maxWidth="md" fullWidth>
//         <DialogTitle>{selectedZone?.zoneName} - Ward Details</DialogTitle>
//         <DialogContent>
//           {selectedZone?.wards?.length ? (
//             <List>
//               {selectedZone.wards.map((ward) => (
//                 <ListItem key={ward.wardID} alignItems="flex-start">
//                   <ListItemText
//                     primary={`${ward.wardName} (Files: ${ward.fileCount})`}
//                     secondary={
//                       ward.fileNames.length ? (
//                         <>
//                           {ward.fileNames.map((fname, i) => (
//                             <Typography key={i} variant="body2" color="text.secondary">
//                               • {fname}
//                             </Typography>
//                           ))}
//                         </>
//                       ) : (
//                         <Typography variant="body2" color="text.secondary">
//                           No files available.
//                         </Typography>
//                       )
//                     }
//                   />
//                 </ListItem>
//               ))}
//             </List>
//           ) : (
//             <DialogContentText>No ward data available.</DialogContentText>
//           )}
//         </DialogContent>
//       </Dialog>
//     </Box>
//   );
// };

// export default ZonePieChart;

